import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Download, ExternalLink, Loader2, Receipt } from "lucide-react";
import { API, useApp } from "@/state/AppContext";

function money(cents, cur) {
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency", currency: (cur || "eur").toUpperCase(),
    }).format((cents || 0) / 100);
  } catch { return `${(cents || 0) / 100} ${cur}`; }
}

function fmtDate(unix) {
  if (!unix) return "";
  return new Date(unix * 1000).toLocaleDateString("de-DE",
    { day: "2-digit", month: "long", year: "numeric" });
}

const STATUS_COLOR = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-amber-50 text-amber-700 border-amber-200",
  void: "bg-slate-50 text-slate-500 border-slate-200",
  uncollectible: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_LABEL = { paid: "Bezahlt", open: "Offen", void: "Storniert",
                       uncollectible: "Ausstehend", draft: "Entwurf" };

export default function InvoicesModal({ open, onClose }) {
  const { authHeaders } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/payments/invoices`, { headers: authHeaders })
      .then(r => setInvoices(r.data.invoices || []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [open, authHeaders]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         data-testid="invoices-modal" role="dialog" aria-modal="true">
      <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Deine Rechnungen</h2>
          </div>
          <button onClick={onClose} data-testid="invoices-close" className="p-2 rounded-full hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Rechnungen …
            </div>
          )}
          {!loading && invoices.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              Noch keine Rechnungen vorhanden.
            </div>
          )}
          {invoices.map(inv => {
            const cls = STATUS_COLOR[inv.status] || STATUS_COLOR.void;
            return (
              <div key={inv.id} className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`invoice-${inv.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {money(inv.amount_paid || inv.amount_due, inv.currency)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {fmtDate(inv.created)}{inv.number ? ` · ${inv.number}` : ""}
                    </div>
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-full font-semibold border ${cls}`}>
                    {STATUS_LABEL[inv.status] || inv.status}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {inv.invoice_pdf && (
                    <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                       data-testid={`invoice-pdf-${inv.id}`}
                       className="min-h-[40px] rounded-xl bg-blue-50 text-blue-700 text-sm font-semibold flex items-center justify-center gap-1.5 border border-blue-100">
                      <Download className="w-4 h-4" /> PDF
                    </a>
                  )}
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`invoice-view-${inv.id}`}
                       className="min-h-[40px] rounded-xl bg-white text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5 border border-slate-200">
                      <ExternalLink className="w-4 h-4" /> Ansehen
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
